/**
 * Chart configuration constants for user charts
 * Separates static metric definitions from dynamic state
 */

export type MetricType =
  | 'speed_in_km/h'
  | 'soe'
  | 'h2mass_in_kg'
  | 'power_at_wheel_in_W'
  | 'energy_counter_withdrawn_in_kWh'
  | 'energy_counter_recuperated_in_kWh'
  | 'available_usable_battery_energy_kWh'
  | 'battery_cycled_energy_kWh'
  | 'traction_force_in_N'
  | 'driving_resistance_in_N'
  | 'net_force_at_wheel_in_N'
  | 'acceleration_in_m_per_s2'
  | 'electric_brake_force_in_N'
  | 'mechanical_braking_force_in_N';

/**
 * Keys that exist in the data but are not plottable metrics (used as axes)
 */
export const NON_METRIC_KEYS: Set<string> = new Set(['time_in_s', 'position_in_m']);

export interface MetricDefinition {
  label: string;
  group: string;
  dataKey: string;
  source: 'simulation';
  unit: string;
  color: string;
}

export interface MetricConfig extends MetricDefinition {
  value: MetricType;
  enabled: boolean;
}

/**
 * Static metric definitions - configuration that never changes
 */
export const METRIC_DEFINITIONS: Record<MetricType, MetricDefinition> = {
  'speed_in_km/h': {
    label: 'Speed',
    group: 'Simulation',
    dataKey: 'speed_in_km/h',
    source: 'simulation',
    unit: 'km/h',
    color: '#0098A6'
  },
  'power_at_wheel_in_W': {
    label: 'Power at Wheel',
    group: 'Simulation',
    dataKey: 'power_at_wheel_in_W',
    source: 'simulation',
    unit: 'W',
    color: '#C4314B'
  },
  soe: {
    label: 'Battery SOE',
    group: 'Simulation',
    dataKey: 'soe',
    source: 'simulation',
    unit: '%',
    color: '#2D8659'
  },
  'energy_counter_withdrawn_in_kWh': {
    label: 'Energy Consumed',
    group: 'Simulation',
    dataKey: 'energy_counter_withdrawn_in_kWh',
    source: 'simulation',
    unit: 'kWh',
    color: '#2B5F8A'
  },
  'energy_counter_recuperated_in_kWh': {
    label: 'Energy Recuperated',
    group: 'Simulation',
    dataKey: 'energy_counter_recuperated_in_kWh',
    source: 'simulation',
    unit: 'kWh',
    color: '#4A90A8'
  },
  'battery_cycled_energy_kWh': {
    label: 'Battery Cycled Energy',
    group: 'Simulation',
    dataKey: 'battery_cycled_energy_kWh',
    source: 'simulation',
    unit: 'kWh',
    color: '#6B7B8A'
  },
  'available_usable_battery_energy_kWh': {
    label: 'Available Battery Energy',
    group: 'Simulation',
    dataKey: 'available_usable_battery_energy_kWh',
    source: 'simulation',
    unit: 'kWh',
    color: '#3D6E8E'
  },
  'h2mass_in_kg': {
    label: 'Hydrogen Mass',
    group: 'Simulation',
    dataKey: 'h2mass_in_kg',
    source: 'simulation',
    unit: 'kg',
    color: '#5C6670'
  },
  'traction_force_in_N': {
    label: 'Traction Force',
    group: 'Detailed',
    dataKey: 'traction_force_in_N',
    source: 'simulation',
    unit: 'N',
    color: '#8B5E3C'
  },
  'driving_resistance_in_N': {
    label: 'Driving Resistance',
    group: 'Detailed',
    dataKey: 'driving_resistance_in_N',
    source: 'simulation',
    unit: 'N',
    color: '#4A6741'
  },
  'net_force_at_wheel_in_N': {
    label: 'Net Force at Wheel',
    group: 'Detailed',
    dataKey: 'net_force_at_wheel_in_N',
    source: 'simulation',
    unit: 'N',
    color: '#6E7B5A'
  },
  'acceleration_in_m_per_s2': {
    label: 'Acceleration',
    group: 'Detailed',
    dataKey: 'acceleration_in_m_per_s2',
    source: 'simulation',
    unit: 'm/s\u00B2',
    color: '#A0522D'
  },
  'electric_brake_force_in_N': {
    label: 'Electric Brake Force',
    group: 'Detailed',
    dataKey: 'electric_brake_force_in_N',
    source: 'simulation',
    unit: 'N',
    color: '#5A7A9A'
  },
  'mechanical_braking_force_in_N': {
    label: 'Mechanical Braking Force',
    group: 'Detailed',
    dataKey: 'mechanical_braking_force_in_N',
    source: 'simulation',
    unit: 'N',
    color: '#7A4F5A'
  }
};

/**
 * Maximum number of distinct Y-axes allowed on a single chart.
 * Metrics sharing the same unit are merged onto one axis.
 */
export const MAX_Y_AXES = 2;

/**
 * Default enabled metrics on component initialization
 */
export const DEFAULT_ENABLED_METRICS: Set<MetricType> = new Set(['speed_in_km/h']);
